import React from 'react';
import { Link, useLocation } from 'react-router-dom';

// AI 아이콘 컴포넌트
const Icon = ({ name, size = 16 }) => {
  const icons = {
    tools: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
      </svg>
    ),
    storyboard: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="3" width="18" height="18" rx="2"/>
        <path d="M3 9h18"/>
        <path d="M9 21V9"/>
      </svg>
    ),
    video: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18"/>
        <path d="M7 2v20"/>
        <path d="M17 2v20"/>
        <path d="M2 12h20"/>
        <path d="M2 7h5"/>
        <path d="M2 17h5"/>
        <path d="M17 17h5"/>
        <path d="M17 7h5"/>
      </svg>
    ),
    upscale: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M15 3h6v6"/>
        <path d="M9 21H3v-6"/>
        <path d="M21 3l-7 7"/>
        <path d="M3 21l7-7"/>
      </svg>
    ),
    audio: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/>
        <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
        <line x1="12" y1="19" x2="12" y2="22"/>
      </svg>
    ),
    summarize: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14 2 14 8 20 8"/>
        <line x1="16" y1="13" x2="8" y2="13"/>
        <line x1="16" y1="17" x2="8" y2="17"/>
        <polyline points="10 9 9 9 8 9"/>
      </svg>
    ),
    clock: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10"/>
        <polyline points="12 6 12 12 16 14"/>
      </svg>
    ),
    collapse: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="11 17 6 12 11 7"/>
        <polyline points="18 17 13 12 18 7"/>
      </svg>
    ),
    studio: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="2" y="3" width="20" height="14" rx="2"/>
        <path d="M8 21h8"/>
        <path d="M12 17v4"/>
        <path d="M7 9l3 3 4-4 3 3"/>
      </svg>
    ),
  };
  return icons[name] || null;
};

const Sidebar = ({ collapsed, onToggle, mobileOpen, onMobileClose }) => {
  const location = useLocation();

  // 현재 활성 경로 확인
  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/' || location.pathname === '/content-tools';
    }
    if (path === '/studio') {
      return location.pathname === '/studio' || location.pathname.startsWith('/studio/');
    }
    return location.pathname === path;
  };

  // 메뉴 항목 정의
  const menuItems = [
    { path: '/', icon: 'tools', label: 'AI 콘텐츠 도구' },
    { path: '/studio', icon: 'studio', label: '스튜디오' },
    { path: '/storyboard', icon: 'storyboard', label: 'AI 스토리보드' },
    { path: '/video-creator', icon: 'video', label: '비디오 생성' },
    { path: '/upscaler', icon: 'upscale', label: '이미지 업스케일' },
    { path: '/audio-analysis', icon: 'audio', label: '오디오 분석' },
    { path: '/content-summarize', icon: 'summarize', label: '콘텐츠 분석' },
  ];

  const handleNavClick = () => {
    if (onMobileClose) {
      onMobileClose();
    }
  };

  return (
    <aside
      className={`flex flex-col h-full bg-white border-r border-gray-200 transition-all duration-200 flex-shrink-0
        ${collapsed ? 'w-14' : 'w-60'}
        ${mobileOpen ? 'translate-x-0 shadow-lg' : ''}
        max-md:fixed max-md:left-0 max-md:top-11 max-md:bottom-0 max-md:h-[calc(100vh-44px)] max-md:h-[calc(100dvh-44px)] max-md:z-[100] max-md:w-[280px] max-md:-translate-x-full max-md:shadow-none max-md:transition-transform max-md:duration-300 max-md:overflow-y-auto max-md:touch-pan-y
        ${mobileOpen ? 'max-md:translate-x-0 max-md:shadow-[4px_0_20px_rgba(0,0,0,0.15)]' : ''}`}
    >
      {/* Header */}
      <div className={`flex items-center justify-between p-4 border-b border-gray-200 min-h-14
        ${collapsed ? 'justify-center px-3.5' : ''}
        max-md:justify-start max-md:px-5 max-md:flex-shrink-0`}>
        {!collapsed && (
          <div className="flex items-center gap-1.5">
            <a href="https://ilouli.com" className="text-sm font-semibold text-gray-900 no-underline tracking-tight hover:text-blue-500 transition-colors">ilouli</a>
            <span className="text-gray-400 font-normal">·</span>
            <Link to="/" className="text-sm font-semibold text-purple-600 no-underline tracking-tight hover:text-purple-700 transition-colors">AI</Link>
          </div>
        )}
        <button
          onClick={onToggle}
          className={`flex items-center justify-center w-7 h-7 p-0 bg-transparent border-none rounded-md cursor-pointer text-gray-400 transition-all duration-150 hover:bg-gray-100 hover:text-gray-900
            ${collapsed ? 'rotate-180' : ''}
            max-md:hidden`}
          aria-label={collapsed ? '사이드바 펼치기' : '사이드바 접기'}
        >
          <Icon name="collapse" size={16} />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 px-2 overflow-y-auto max-md:flex-1 max-md:overflow-y-auto max-md:touch-pan-y max-md:pb-5">
        {menuItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`flex items-center gap-2.5 w-full py-2.5 px-3 border-none rounded-lg bg-transparent no-underline text-sm font-medium cursor-pointer transition-all duration-150 text-left mb-0.5
              ${collapsed ? 'justify-center px-2.5' : ''}
              ${isActive(item.path)
                ? 'bg-purple-500/10 text-purple-600'
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              }
              max-md:min-h-11 max-md:py-3 max-md:px-4
              ${collapsed ? 'max-md:justify-start max-md:py-2.5 max-md:px-3' : ''}`}
            title={collapsed ? item.label : undefined}
            onClick={handleNavClick}
          >
            <span className={`flex items-center justify-center w-5 h-5 flex-shrink-0 transition-colors duration-150
              ${isActive(item.path) ? 'text-purple-600' : 'text-gray-400'}
              ${collapsed ? 'm-0' : ''}`}>
              <Icon name={item.icon} />
            </span>
            {!collapsed && <span className="flex-1 whitespace-nowrap overflow-hidden text-ellipsis">{item.label}</span>}
          </Link>
        ))}
      </nav>

      {/* Spacer */}
      <div className="flex-1 max-md:hidden"></div>
    </aside>
  );
};

export default Sidebar;
